import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Spin, Switch } from "antd";
import { CheckCircleFilled, FileAddOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";

import PatientInfoCard, { sectionHeading } from "../TreatmentPlans/component/shared/PatientInfoCard";
import { describeMember, describeMissing, missingCriticalFields } from "../TreatmentPlans/lib/policyCompleteness";
import { isPendingId, pendingLabel } from "./pendingPolicies";
import { PolicyReviewBody, reviewState, usePolicyReview } from "./PolicyReview";

/**
 * The Policies step — which policies, each reviewed and approved where it opens.
 *
 * Picking a policy opens it in place: the fields the carrier verifies against,
 * any PMS or claim-form suggestions beside them, and Approve. Approving saves
 * and folds the card shut, and the next picked policy that still needs the user
 * opens. There is no separate review step any more — the decision and the check
 * sit in the same card, so nothing is chosen in one place and confirmed in
 * another.
 *
 * Policies are presented as objects you pick up, with their gaps stated plainly
 * before you commit. Nothing here mentions whether a carrier has an agent: who
 * services the request is our problem, and surfacing it would only ask the office
 * to make a choice they have no basis for.
 */
export default function PolicySelectStep({
  patient,
  policies,
  loadingPolicies,
  selectedIds,
  onToggle,
  drafts,
  approved,
  // Suggestions per saved policy ({ [policyId]: preview }) — the PMS's, and a
  // claim form's for a plan the patient already has. Pending cards carry theirs.
  previews,
  onDraftChange,
  onApproved,
  onSavePending,
  // A server rejection names a policy to fix; open it.
  focusPolicyId,
  // One line describing the uploaded claim form that goes with the request.
  claimFormSummary,
  urgent,
  onUrgent,
  onAddPolicy,
  // Adding a policy happens IN this step rather than in a modal over the drawer:
  // the host owns the state and hands us the rendered form.
  addingPolicy,
  renderAddPolicyForm,
  // Anything the host shows under the list (the office's CDA/iTrans prompt).
  footer,
}) {
  const list = policies || [];
  const selected = selectedIds || [];
  const approvedMap = approved || {};
  // Added by hand and not linked to a PMS record yet (the next sync links them):
  // there is nothing to look up, and "none came back from your PMS" would be
  // misleading. Only an explicit false — a row that doesn't say is a PMS patient.
  const notInPms = !!patient && patient.from_pms === false;

  const review = usePolicyReview({ policies: list, drafts, onApproved, onSavePending });
  const [expanded, setExpanded] = useState(null);
  const previousSelection = useRef(selected);

  // Open what was just picked; when the open card is approved, move to the next
  // picked one that still needs the user — never yank them out of one they are
  // still working in.
  useEffect(
    function () {
      const before = previousSelection.current;
      previousSelection.current = selected;
      const added = selected.filter(function (id) { return before.indexOf(id) === -1; });
      setExpanded(function (current) {
        if (added.length) return added[added.length - 1];
        if (current !== null && selected.indexOf(current) !== -1 && !approvedMap[current]) return current;
        const next = selected.find(function (id) { return !approvedMap[id]; });
        return next === undefined ? null : next;
      });
    },
    [selected.join("|"), Object.keys(approvedMap).join("|")], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    function () {
      if (focusPolicyId != null) setExpanded(focusPolicyId);
    },
    [focusPolicyId],
  );

  function handleCardClick(policy) {
    // Picking is the first click; after that the card opens and closes, and only
    // the checkbox un-picks — a stray click must not throw away a review.
    if (selected.indexOf(policy.id) === -1) {
      onToggle(policy.id);
      return;
    }
    setExpanded(expanded === policy.id ? null : policy.id);
  }

  return (
    <div>
      {/* The same read-only card the insurance policy form shows, so the details
          you are about to verify against are on screen while you pick a policy. */}
      {patient ? (
        <div className="ir-patient-card">
          <PatientInfoCard patient={patient} />
        </div>
      ) : null}

      {claimFormSummary ? (
        <div className="ir-claim-form-line">
          <FileTextOutlined />
          <span>
            <strong>From the claim form:</strong> {claimFormSummary}
          </span>
        </div>
      ) : null}

      <div className="ir-step-heading">{sectionHeading("Select Policy")}</div>

      {loadingPolicies ? (
        <div style={{ padding: "22px 0", display: "flex", alignItems: "center", gap: 10 }}>
          <Spin size="small" />
          <span className="ir-hint" style={{ margin: 0 }}>
            {notInPms
              ? "Checking this patient's insurance…"
              : "Looking up this patient's insurance in your practice management system…"}
          </span>
        </div>
      ) : list.length === 0 && !addingPolicy ? (
        // A dead end unless we offer the way out right here: offices with no
        // working PMS connection will ALWAYS land on this, so entering the policy
        // by hand is the primary action, not a link buried in a sentence.
        <div className="ir-panel-state" style={{ border: "1px solid #ece8f4", borderRadius: 8 }}>
          <div className="ir-panel-icon"><FileAddOutlined /></div>
          <p className="ir-panel-title">{notInPms ? "Not in your PMS yet" : "No insurance on file yet"}</p>
          <p className="ir-panel-sub" style={{ marginBottom: 16 }}>
            {notInPms
              ? "This patient was added in Smilepass, so there's no PMS record to fetch "
                + "their insurance from. Add their policy details to continue — they'll be "
                + "linked to their PMS record after its next sync."
              : "We couldn't find a policy for this patient, and none came back from your "
                + "practice management system. Add their policy details to continue."}
          </p>
          <Button type="primary" onClick={onAddPolicy}>
            Add a policy
          </Button>
        </div>
      ) : (
        <>
          {list.map(function (policy) {
            const isSelected = selected.indexOf(policy.id) !== -1;
            const isApproved = !!approvedMap[policy.id];
            const open = isSelected && expanded === policy.id;
            const merged = review.effective(policy);
            const missing = missingCriticalFields(merged);
            const cls = [
              "ir-policy",
              isSelected ? "is-selected" : "",
              isApproved ? "is-approved" : "",
              !isSelected && missing.length ? "is-incomplete" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <div key={policy.id} className={cls}>
                <div
                  className="ir-policy-top"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isSelected ? open : undefined}
                  onClick={function () { handleCardClick(policy); }}
                  onKeyDown={function (e) {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCardClick(policy);
                    }
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onClick={function (e) { e.stopPropagation(); }}
                    onChange={function () { onToggle(policy.id); }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ir-policy-carrier">
                      {policy.insurance_name_display || policy.carrier_display_name || "Policy"}
                      {isPendingId(policy.id) ? (
                        // Not a policy yet: the PMS or the form has it, we haven't saved it.
                        <span className="ir-check-ordinal ir-pending-pill">{pendingLabel(policy)}</span>
                      ) : null}
                    </div>
                    <div className="ir-policy-meta">
                      {policy.insurance_type_display || policy.insurance_type}
                      {/* Who the member is: a patient's own plan and their spouse's
                          can share a carrier, and without this the two cards read
                          as one policy listed twice. */}
                      {describeMember(merged) ? " · " + describeMember(merged) : ""}
                      {merged.member_id ? " · Member " + merged.member_id : ""}
                    </div>
                  </div>
                  {isSelected ? (
                    <span
                      className={"ir-policy-state" + (isApproved
                        ? " is-done"
                        : missing.length || review.needsCarrier(policy) ? " is-gap" : "")}
                    >
                      {isApproved ? <CheckCircleFilled /> : null}
                      {reviewState(policy, review, isApproved)}
                    </span>
                  ) : null}
                </div>
                {!isSelected && missing.length ? (
                  <div className="ir-policy-gap">
                    Needs {describeMissing(missing)} — you'll be asked for {missing.length > 1 ? "these" : "this"} when you pick it.
                  </div>
                ) : null}
                {open ? (
                  <div className="ir-policy-review">
                    <PolicyReviewBody
                      policy={policy}
                      review={review}
                      drafts={drafts}
                      isApproved={isApproved}
                      preview={previews && previews[policy.id]}
                      patientId={patient && patient.id}
                      patient={patient}
                      onChange={onDraftChange}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* The list is never the whole story: what the PMS holds can be stale or
              wrong, so entering a policy by hand stays available alongside the
              existing ones — ghost, not filled, so it doesn't compete with the
              primary act of picking one. */}
          {addingPolicy ? (
            <div className="ir-policy-form">
              <div className="ir-policy-form-head">New policy</div>
              {renderAddPolicyForm ? renderAddPolicyForm() : null}
            </div>
          ) : (
            <button type="button" className="ir-add-policy" onClick={onAddPolicy}>
              <PlusOutlined />
              Add a new policy
            </button>
          )}
        </>
      )}

      {footer}

      <div
        style={{
          marginTop: 22,
          paddingTop: 18,
          borderTop: "1px solid #ece8f4",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Switch size="small" checked={urgent} onChange={onUrgent} />
        <div>
          <div style={{ fontSize: 14, color: "#2b2440", fontWeight: 600 }}>Mark Urgent</div>
          <div style={{ fontSize: 12.5, color: "#848696", lineHeight: 1.45 }}>
            We will do our best to start on this task immediately and deliver within 20 minutes.
          </div>
        </div>
      </div>
    </div>
  );
}
